import { ChildInput } from '@msc-b2c/interfaces/types';
import {
  FormTypeEnum,
  getAdultAgePerMarketMapping,
  getCountryCode,
  getCountryCodeISO3,
  validateFormField,
} from '@msc-b2c/shared-utils';
import { ReactNode } from 'react';
import {
  AddressSearchInput,
  BaseSelect,
  CheckboxForm,
  DatePickerInput,
  Hidden,
  InputText,
  PhoneInput,
  Text,
} from '../../../index';
import { Controller, UseFormReturn } from 'react-hook-form';

export const getFormChildren = (
  guestTypeDataForm: ChildInput[],
  {
    register,
    resetField,
    formState: { errors },
    trigger,
    clearErrors,
    getValues,
    control,
    setError,
    setValue,
    watch,
  }: UseFormReturn,
  configs?: { enableCheckboxTitleAndSubtitle: boolean }
): ReactNode[] =>
  guestTypeDataForm.map((fieldForm, index) => {
    switch (fieldForm.type) {
      case FormTypeEnum.TEXT:
      case FormTypeEnum.EMAIL:
        return (
          <InputText
            key={`input-field-form-${index}--${fieldForm.name}`}
            register={register}
            errors={errors}
            index={index}
            label={fieldForm.label}
            placeholder={fieldForm.helpMessage ?? ''}
            name={fieldForm.name}
            validation={{
              validate: (v) => validateFormField(v, fieldForm.validationRulesContainer || []),
              onBlur: () => trigger(`${fieldForm.name}`),
            }}
            disabled={false}
            clearErrors={clearErrors}
            preventCopyPaste={fieldForm.name === 'confirmEmail'}
            styleOptions={fieldForm.appliedCssClassNames}
          />
        );
      case FormTypeEnum.ADDRESS_SEARCH:
        return (
          <AddressSearchInput
            key={`address-search-field-form-${index}--${fieldForm.name}`}
            label={fieldForm.label}
            secondLabel={fieldForm.secondLabel ?? ''}
            name={fieldForm.name}
            secondName={fieldForm.secondName ?? ''}
            placeholder={fieldForm.helpMessage ?? ''}
            secondPlaceholder={fieldForm.secondHelpMessage ?? ''}
            verifyZipCodeButtonLabel={fieldForm?.verifyZipCodeButtonLabel}
            zipCodeValidationErrorMessage={fieldForm?.zipCodeValidationErrorMessage}
            cityValidationErrorMessage={fieldForm?.cityValidationErrorMessage}
            clearErrors={clearErrors}
            errors={errors}
            setError={setError}
            getValues={getValues}
            setValue={setValue}
            index={index}
            register={register}
            resetField={resetField}
            watch={watch}
            validationRulesContainer={fieldForm.validationRulesContainer}
            trigger={trigger}
            styleOptions={fieldForm.appliedCssClassNames}
          />
        );
      case FormTypeEnum.DATE: {
        const minimumAge =
          getAdultAgePerMarketMapping().find(
            (config) => getCountryCode && config.marketCode === getCountryCode()
          )?.adultAge ?? 18;
        return (
          <DatePickerInput
            key={`date-field-form-${index}--${fieldForm.name}`}
            control={control}
            errors={errors}
            index={index}
            label={fieldForm.label}
            name={fieldForm.name}
            clearErrors={clearErrors}
            setError={setError}
            setValue={setValue}
            minAge={minimumAge}
            validation={{
              validate: (v: any) =>
                validateFormField(v, fieldForm.validationRulesContainer || [], {
                  minimumAgeDateOfBirth: minimumAge,
                }),
              onBlur: () => trigger(`${fieldForm.name}`),
            }}
            disabled={false}
            styleOptions={fieldForm.appliedCssClassNames}
          />
        );
      }
      case FormTypeEnum.DROPDOWN:
        return (
          <Controller
            key={`controller-field-form-${index}--${fieldForm.name}`}
            name={fieldForm.name}
            control={control}
            rules={{
              required: fieldForm.isMandatory ? fieldForm.isMandatoryErrorMessage : false,
            }}
            defaultValue={
              fieldForm.isCoRPrefilledAndDisabled
                ? fieldForm.dropdownItems?.find(
                    (c) => c.value === getCountryCode() || c.value === getCountryCodeISO3()
                  )?.value
                : ''
            }
            render={({ field }) => (
              <BaseSelect
                label={fieldForm.label}
                placeholder={fieldForm.placeHolderSelect ?? ''}
                ariaLabel={fieldForm.label}
                items={fieldForm.dropdownItems.sort((a, b) => {
                  const isCountryCodeA =
                    a.value === getCountryCodeISO3() || a.value === getCountryCode();
                  const isCountryCodeB =
                    b.value === getCountryCodeISO3() || b.value === getCountryCode();

                  if (isCountryCodeA) return -1;
                  if (isCountryCodeB) return 1;
                  return 0;
                })}
                errors={errors}
                value={String(field.value ?? '')}
                fieldName={field.name}
                onChange={field.onChange}
                onBlur={() => trigger(`${fieldForm.name}`)}
                disabled={fieldForm.isCoRPrefilledAndDisabled || fieldForm.readOnly === true}
                clearErrors={clearErrors}
              />
            )}
          />
        );

      case FormTypeEnum.PHONE:
        return (
          <PhoneInput
            key={`phone-field-form-${index}-${fieldForm.name}`}
            control={control}
            errors={errors}
            index={index}
            label={fieldForm.label}
            name={fieldForm.name}
            clearErrors={clearErrors}
            setValue={setValue}
            validationRulesContainer={fieldForm.validationRulesContainer}
            disabled={false}
            prefixName={`prefix`}
            placeholder={fieldForm.helpMessage ?? ''}
            register={register}
            trigger={trigger}
          />
        );
      case FormTypeEnum.CHECKBOX:
        return fieldForm?.items?.map((items, itemIndex) => {
          return (
            <CheckboxForm
              key={`checkbox-field-form-${index}-${itemIndex}-${fieldForm.name}`}
              index={Number(`${index}${itemIndex}`)}
              name={fieldForm.name}
              label={items.text}
              register={register}
              errors={errors}
              clearErrors={clearErrors}
              disabled={fieldForm.readOnly}
              checked={items.selected === 'true'}
              styleOptions={fieldForm.appliedCssClassNames}
              title={fieldForm.label}
              enableCheckboxTitleAndSubtitle={configs?.enableCheckboxTitleAndSubtitle}
            />
          );
        });
      case FormTypeEnum.HIDDEN:
        return (
          <Hidden
            key={`input-field-form-${index}-${fieldForm.name}`}
            index={index}
            name={fieldForm.name}
            register={register}
            value={fieldForm.value}
          />
        );
      case FormTypeEnum.TEXT_COMPONENT:
        return (
          <Text
            text={`${fieldForm.text}`}
            styleOption={fieldForm.appliedCssClassNames}
          />
        );
      default:
        return null;
    }
  });
